import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { useAppConfig } from "@/appConfig";
import { copyToClipboard } from "@/helpers/clipboard";
import { trackEvent } from "@/services/analytics";
import {
  gitchaExplainerUrl,
  trackGitchaExplainerClick,
} from "@/helpers/gitchaExplainer";
import ShareCard from "../MemberWantAd/v2/ShareCard";
import "./GitchaShareAccess.scss";

const MEMBERS = "members";
const EVERYONE = "everyone";

const Icon = ({ kind }) => {
  const common = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.9,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };
  if (kind === "members") {
    return (
      <svg {...common}>
        <rect x="5" y="11" width="14" height="10" rx="2.5" />
        <path d="M8 11V8a4 4 0 0 1 8 0v3" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3.2 9.5h17.6M3.2 14.5h17.6M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
    </svg>
  );
};

const GitchaShareAccess = ({
  show,
  onHide,
  initialAudience = MEMBERS,
  isSyndicated,
  memberUrl,
  wantAdId,
  adDetails,
  shareUrl,
  fbUrl,
  imageUrl,
  onSyndicate,
  isSyndicating,
}) => {
  const { tenant } = useAppConfig() || {};
  const network = tenant?.mlsShortName || "your MLS or association";
  const [audience, setAudience] = useState(initialAudience);

  useEffect(() => {
    if (show) setAudience(initialAudience);
  }, [show, initialAudience]);

  const track = (name) =>
    trackEvent({ name, attributes: { wantAdId, audience, isSyndicated: !!isSyndicated } });

  const options = [
    {
      key: MEMBERS,
      title: "Members only",
      body: `Anyone in ${network} with the link can open\u00a0it.`,
    },
    {
      key: EVERYONE,
      title: "Everyone",
      body: isSyndicated
        ? "Anyone with the link can view it on\u00a0Gitcha.com."
        : "Not syndicated yet. Syndicate it so anyone can view\u00a0it.",
    },
  ];

  const publicReady = audience === EVERYONE && isSyndicated;

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      className="gitcha-share-access modal-width-548"
    >
      <Modal.Body>
        <div className="gitcha-share-access__head">
          <h2>Share this Want&#8209;Listing</h2>
          <button type="button" aria-label="Close" onClick={onHide}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <p className="gitcha-share-access__lede">Choose who can open the link.</p>

        <div className="gitcha-share-access__options" role="radiogroup" aria-label="Who can open the link">
          {options.map((opt) => {
            const active = audience === opt.key;
            return (
              <button
                type="button"
                key={opt.key}
                role="radio"
                aria-checked={active}
                className={`gitcha-share-access__option${active ? " is-active" : ""}`}
                onClick={() => {
                  setAudience(opt.key);
                  track("gitcha_share_audience_selected");
                }}
              >
                <span className="gitcha-share-access__icon">
                  <Icon kind={opt.key === MEMBERS ? "members" : "everyone"} />
                </span>
                <span className="gitcha-share-access__copy">
                  <b>{opt.title}</b>
                  <span>{opt.body}</span>
                </span>
              </button>
            );
          })}
        </div>

        {audience === MEMBERS && (
          <div className="gitcha-share-access__panel">
            <div className="gitcha-share-access__link">
              <input readOnly value={memberUrl} aria-label="Member link" />
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  if (copyToClipboard(memberUrl)) {
                    toast.success("Link copied to clipboard");
                    track("gitcha_share_member_link_copied");
                  }
                }}
              >
                Copy link
              </button>
            </div>
            <p className="gitcha-share-access__note">
              Posting this link on social media will not work for anyone outside{" "}
              {network}. They will be asked to sign in as a&nbsp;member.
            </p>
            <button
              type="button"
              className="gitcha-share-access__switch"
              onClick={() => {
                setAudience(EVERYONE);
                track("gitcha_share_switch_to_everyone");
              }}
            >
              Want anyone to open it? Share with everyone&nbsp;&rarr;
            </button>
          </div>
        )}

        {audience === EVERYONE && !isSyndicated && (
          <div className="gitcha-share-access__panel gitcha-share-access__turnon">
            <h3>
              Syndicate to <b className="gitcha-brand">Gitcha.com</b>
            </h3>
            <p>
              Your Want&#8209;Listing gets a public page and a share card with your
              branding on&nbsp;it. You can stop syndicating at any&nbsp;time.
            </p>
            <button
              type="button"
              className="btn btn-primary w-100"
              disabled={isSyndicating}
              onClick={() => {
                track("gitcha_share_syndicate_clicked");
                onSyndicate();
              }}
            >
              {isSyndicating ? "Syndicating..." : "Syndicate"}
            </button>
            <a
              href={gitchaExplainerUrl("share_access_modal")}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackGitchaExplainerClick("share_access_modal")}
            >
              How Gitcha.com works for buyer&nbsp;agents&nbsp;&rarr;
            </a>
          </div>
        )}

        {publicReady && (
          <div className="gitcha-share-access__panel gitcha-share-access__public">
            <ShareCard
              wantAdId={wantAdId}
              isSideCard
              hideHeader
              adDetails={adDetails}
              shareLink={shareUrl}
              fbLink={fbUrl}
              imageLink={imageUrl}
              fromWantAdDetails
            />
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default GitchaShareAccess;
